import Image from 'next/image'
import Router from 'next/router'
import { ComponentPropsWithoutRef, useRef, useState } from 'react'
import { useClient, useQuery } from 'urql'

import { SignOutMutation } from '@/queries'
import { graphql } from '@/school-gql'
import { logout } from '@/school-urql'
import { getAuthentication } from '@/urql-common'
import { cn } from '@/utils/tw-helpers'
import { SignUpFlowModal } from '../modal'
import { SchoolTopHeaderMenu } from './SchoolTopHeaderMenu'

const SchoolWebTopHeader_Query = graphql(`
  query SchoolWebTopHeader_Query {
    viewer {
      id
      email
      firstName
      lastName
    }
  }
`)

interface SchoolWebTopHeaderProps extends ComponentPropsWithoutRef<'header'> {
  title?: string
  showRight?: boolean
}

export const SchoolWebTopHeader = (
  props: SchoolWebTopHeaderProps
): JSX.Element => {
  const { showRight = true, className, ...rest } = props
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false)

  const client = useClient()
  const isAuthenticated = getAuthentication()

  const [{ data: rawData }] = useQuery({
    query: SchoolWebTopHeader_Query,
    pause: !isAuthenticated,
  })

  // Keep last known viewer data — never flash back to undefined
  const viewerRef = useRef(rawData?.viewer)
  if (rawData?.viewer) viewerRef.current = rawData.viewer
  const viewer = viewerRef.current

  const loginId = viewer?.email || ''
  const firstName = viewer?.firstName || ''
  const lastName = viewer?.lastName || ''

  const handleSignOutClick = async function () {
    const refreshToken = isAuthenticated?.refreshToken
    if (!refreshToken) return

    await client.mutation(SignOutMutation, { refreshToken }).toPromise()
    await logout()
  }

  return (
    <>
      <header
        className={cn(
          'flex h-[50px] items-center bg-white px-2 md:h-[60px] md:px-3 lg:h-[70px] lg:px-6',
          className
        )}
        {...rest}
      >
        <div className="md:flex-1">
          <Image
            src="/gApp_logo.svg"
            width={196}
            height={52}
            alt="LogiQlabo"
            className="h-8 w-[120px] md:h-10 md:w-[150px] lg:h-[52px] lg:w-[196px]"
          />
        </div>
        <div className="flex-1">
          {!!props.title && (
            <h2 className="text-center text-lg font-bold text-[#A2A2A2]">
              {props.title}
            </h2>
          )}
        </div>
        <div className="flex flex-1 justify-end">
          {showRight && (
            <>
              {isAuthenticated ? (
                viewer ? (
                  <SchoolTopHeaderMenu
                    loginId={loginId}
                    firstName={firstName}
                    lastName={lastName}
                    handleSignOutClick={handleSignOutClick}
                  />
                ) : null
              ) : (
                <>
                  <button
                    className="g-button g-app-button--primary !h-[44px] w-[130px] !rounded-full !text-sm !shadow-none"
                    onClick={() => setIsOpenModal(true)}
                  >
                    申し込む
                  </button>
                  <button
                    className="g-button g-button--primary ml-[14px] !h-[44px] w-[130px] !rounded-full !text-sm !shadow-none"
                    onClick={() => Router.push('/web/sign_in')}
                  >
                    ログイン
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </header>

      <SignUpFlowModal
        isOpen={isOpenModal}
        onRequestClose={() => setIsOpenModal(false)}
      />
    </>
  )
}
